import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ProposalAIAssistantProps {
  onContentGenerated: (content: any, action: string) => void;
  context?: any;
}

export function ProposalAIAssistant({ onContentGenerated, context }: ProposalAIAssistantProps) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  const quickActions = [
    { label: "Generate Cover Page", action: "generate_cover" },
    { label: "Write Scope of Work", action: "generate_scope" },
    { label: "Create Terms", action: "generate_terms" },
    { label: "Write Marketing Copy", action: "generate_marketing" },
  ];

  const handleGenerate = async (action: string, customPrompt?: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("proposal-ai-assistant", {
        body: {
          action,
          context: {
            ...context,
            prompt: customPrompt || prompt,
          },
        },
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      onContentGenerated(data.content, data.action || action);
      toast.success("Content generated successfully!");
      setPrompt("");
    } catch (error) {
      console.error("AI generation error:", error);
      toast.error("Failed to generate content");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-primary" />
        <h3 className="font-semibold">AI Assistant</h3>
      </div>

      <div className="space-y-2">
        <Textarea
          placeholder="Ask AI to generate content... (e.g., 'Write a professional introduction')"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          disabled={loading}
        />
        <Button
          onClick={() => handleGenerate("custom")}
          disabled={!prompt || loading}
          className="w-full"
          size="sm"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate
            </>
          )}
        </Button>
      </div>

      <div className="border-t pt-4 space-y-2">
        <p className="text-sm font-medium text-muted-foreground">Quick Actions:</p>
        <div className="grid grid-cols-1 gap-2">
          {quickActions.map((qa) => (
            <Button
              key={qa.action}
              variant="outline"
              size="sm"
              onClick={() => handleGenerate(qa.action)}
              disabled={loading}
              className="justify-start"
            >
              {qa.label}
            </Button>
          ))}
        </div>
      </div>
    </Card>
  );
}
